'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FilterConfig, FilterValue } from '@terra/ui-design-system';
import type { AdGraphExplorePresetDto } from '@terra/shared-contracts-internal-network';
import {
  INTERNAL_NETWORK_GRAPH_EXPLORE_PRESET_LABELS,
  INTERNAL_NETWORK_GRAPH_EXPLORE_PRESETS,
} from '../../utils/internal-network-graph.utils';

const SAVED_QUERY_FILTER_ID = 'savedQuery';

function extractFilterValue(
  filterValue: FilterValue,
  id: string,
): string | undefined {
  return filterValue.find((filter) => filter.id === id)?.value[0];
}

function isAdGraphExplorePreset(
  value: string,
): value is AdGraphExplorePresetDto {
  return INTERNAL_NETWORK_GRAPH_EXPLORE_PRESETS.some(
    (preset) => preset === value,
  );
}

export function useInternalNetworkGraphFilters({
  activePreset,
  crownJewelCount,
  ownedPrincipalCount = 0,
  onRunPreset,
  onResetExplore,
}: {
  activePreset: AdGraphExplorePresetDto | null;
  crownJewelCount: number;
  ownedPrincipalCount?: number;
  onRunPreset: (preset: AdGraphExplorePresetDto) => void;
  onResetExplore: () => void | Promise<void>;
}) {
  const [filterValue, setFilterValue] = useState<FilterValue>([]);

  useEffect(() => {
    if (activePreset) {
      setFilterValue([{ id: SAVED_QUERY_FILTER_ID, value: [activePreset] }]);
      return;
    }

    setFilterValue((current) =>
      current.some((entry) => entry.id === SAVED_QUERY_FILTER_ID)
        ? current.filter((entry) => entry.id !== SAVED_QUERY_FILTER_ID)
        : current,
    );
  }, [activePreset]);

  const filters = useMemo<FilterConfig[]>(
    () => [
      {
        id: SAVED_QUERY_FILTER_ID,
        items: INTERNAL_NETWORK_GRAPH_EXPLORE_PRESETS.map((preset) => ({
          value: preset,
          label: INTERNAL_NETWORK_GRAPH_EXPLORE_PRESET_LABELS[preset],
          disabled:
            (preset === 'crown-jewels' && crownJewelCount === 0) ||
            (preset === 'owned-to-targets' && ownedPrincipalCount === 0),
        })),
        placeholder: 'Saved query',
      },
    ],
    [crownJewelCount, ownedPrincipalCount],
  );

  const handleFilterChange = useCallback(
    (value: FilterValue) => {
      setFilterValue(value);
      const preset = extractFilterValue(value, SAVED_QUERY_FILTER_ID);
      if (!preset || !isAdGraphExplorePreset(preset)) {
        void onResetExplore();
        return;
      }
      onRunPreset(preset);
    },
    [onResetExplore, onRunPreset],
  );

  return {
    filters,
    filterValue,
    onFilterChange: handleFilterChange,
  };
}
